import {useCallback, useMemo, useRef, useState} from 'react';
import assert from 'assert-ts';
import {Concept} from 'types';
import type {CodeListId, Schema} from 'schemaDefinition/types';
import type {Prize} from 'services/prizes/types';
import {useLocalization} from 'localization';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import {useCodelists} from 'services/codelists';
import {arePrizesEqual} from 'services/prizes';
import {getErrorMessage} from 'services/utils/getErrorMessage';
import {useSnacks} from 'components';
import {validate} from 'schema/form/functions/validators/validate';
import {useSchemaCodelistIds} from 'schema/hooks';
import {useSchema} from 'schemas/hooks';
import type {EditorMode} from '../../types';
import type {EditPrizeProps} from '../types';

type EditPrizeState = {
  schema: Schema;
  codelistIds: (CodeListId | LinkedRoleCodeListId)[];
  mode: EditorMode;
  // Initial value of prize when going to edit mode
  initialEditPrize: Prize;
  hasChanges: boolean;
  showErrors: boolean;
  onChanged: (prize: Prize) => void;
  // Consider support for confirm save with warnings
  savePrize: () => Promise<void>;
  deletePrize?: () => Promise<void>;
};

export const useEditPrizeState = (props: EditPrizeProps): EditPrizeState => {
  const {onSave, onDelete} = props;
  const originalValue = useMemo(
    () => props.originalValue ?? {name: props.suggestion},
    [props.originalValue, props.suggestion],
  );
  const mode: EditorMode = originalValue ? 'edit' : 'register';
  const localization = useLocalization();
  const {t} = localization;
  const schema = useSchema(Concept.prize);
  const codelistIds = useSchemaCodelistIds(schema);
  const codelistMap = useCodelists(codelistIds);

  const [initialEditPrize, setInitialEditPrize] = useState<Prize>(
    originalValue as Prize,
  );

  const editedPrizeRef = useRef<Prize>(initialEditPrize);
  const [hasChanged, setHasChanged] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const {successSnack, warningSnack, errorSnack} = useSnacks();

  const handleChanged = useCallback(
    (prize: Prize) => {
      editedPrizeRef.current = prize;
      setHasChanged(!arePrizesEqual(initialEditPrize, editedPrizeRef.current));
    },
    [initialEditPrize],
  );

  const handleSave = useCallback((): Promise<void> => {
    const validation = validate(
      editedPrizeRef.current,
      undefined,
      schema,
      assert(codelistMap, 'useEditPrizeWizard: codelistMap is required'),
    );

    if (validation.valid !== 'valid') {
      setShowErrors(true);
      return Promise.resolve();
    }

    return onSave(assert(editedPrizeRef.current))
      .then(savedPrize => {
        successSnack(t('page.prize.save.success'));

        setInitialEditPrize(savedPrize);
        editedPrizeRef.current = savedPrize;
        setHasChanged(false);
      })
      .catch(error => {
        const message = getErrorMessage(
          error,
          'page.prize.save.failed',
          localization,
        );
        errorSnack(message);
      });
  }, [schema, codelistMap, onSave, successSnack, t, localization, errorSnack]);

  const handleDelete = useCallback((): Promise<void> => {
    if (onDelete === undefined) {
      return Promise.resolve();
    }

    if (editedPrizeRef.current.winners.length > 0) {
      warningSnack(t('page.prize.delete.warning.nonEmptyWinners'));
      return Promise.resolve();
    }

    return onDelete(assert(editedPrizeRef.current).id)
      .then(() => {
        successSnack(t('page.prize.delete.success'));
      })
      .catch(error => {
        const message = getErrorMessage(
          error,
          'page.prize.delete.failed',
          localization,
        );
        errorSnack(message);
      });
  }, [onDelete, warningSnack, t, successSnack, localization, errorSnack]);

  return useMemo(() => {
    return {
      schema,
      codelistIds,
      mode,
      // Initial value of prize when going to edit mode
      initialEditPrize,
      hasChanges: mode === 'register' || hasChanged,
      showErrors,
      onChanged: handleChanged,
      savePrize: handleSave,
      deletePrize: onDelete ? handleDelete : undefined,
    };
  }, [
    schema,
    codelistIds,
    mode,
    initialEditPrize,
    hasChanged,
    showErrors,
    handleChanged,
    handleSave,
    onDelete,
    handleDelete,
  ]);
};
